import { useParams } from "react-router-dom";

import {
  Empty,
  ErrorState,
  InlineNotice,
  Loading,
} from "../../components/WorkspacePrimitives";
import {
  useAgentCompanies,
  useAgentModelVersions,
  useAgentRuns,
} from "../api/e2e";
import AgentModelHistory from "../components/AgentModelHistory";
import CompanyWorkspacePage from "../components/CompanyWorkspacePage";

export default function AgentCompanyDetailPage() {
  const { companyId = "" } = useParams<{ companyId: string }>();
  const companies = useAgentCompanies();
  const versions = useAgentModelVersions(companyId, true);
  const runs = useAgentRuns();
  const company = companies.data?.find((item) => item.id === companyId);

  if (
    (companies.isPending && !companies.data) ||
    (versions.isPending && !versions.data)
  ) {
    return <Loading label="Loading company models…" />;
  }
  if (companies.error && !companies.data) {
    return (
      <ErrorState error={companies.error} onRetry={() => companies.refetch()} />
    );
  }
  if (versions.error && !versions.data) {
    return (
      <ErrorState error={versions.error} onRetry={() => versions.refetch()} />
    );
  }
  if (!company) {
    return <Empty>This company is not available.</Empty>;
  }
  const models = versions.data ?? [];
  const modelIds = new Set(models.map((model) => model.id));
  const pendingRuns = (runs.data ?? []).filter((run) => {
    if (run.company_id !== companyId) return false;
    if (run.state.kind === "queued" || run.state.kind === "active") {
      return true;
    }
    if (run.state.kind !== "terminal") return false;
    return (
      run.state.outcome === "succeeded" &&
      run.model_version_id !== null &&
      !modelIds.has(run.model_version_id)
    );
  });
  const hasModelRows = models.length > 0 || pendingRuns.length > 0;

  return (
    <CompanyWorkspacePage companies={companies.data ?? []} company={company}>
      {runs.error && (
        <InlineNotice tone="warning">
          Active model runs could not refresh. Published versions remain
          available.
        </InlineNotice>
      )}

      <section className="company-tab-content" aria-label="Published models">
        {!hasModelRows ? (
          <Empty>No model has been started for this company.</Empty>
        ) : (
          <AgentModelHistory
            companyId={company.id}
            models={models}
            pendingRuns={pendingRuns}
          />
        )}
      </section>
    </CompanyWorkspacePage>
  );
}
